import Swal from 'sweetalert2';
import api from './api';
import '../styles/sameNameFiles.css';

/**
 * "You sent a file with this name before" for chat documents and zips
 * (server/utils/sameNameFiles.js).
 *
 * Before a message with documents goes out, the server is asked whether this
 * person has sent a file of the same name in this chat (the match is on the
 * name alone). For each that has, every earlier version is listed, newest
 * first - "update.zip", "update(1).zip"... with date and size, and "Same size
 * as yours" where the size matches exactly - and they choose: send one of
 * those again (nothing uploads), or upload this one under the next number
 * ("update(2).zip"). Photos, videos and voice notes are not checked.
 *
 * resolveSameNameFiles(conversationId, files) ->
 *   { files, reuse }   files to upload (renamed where chosen) and earlier files to send again
 *   null               they pressed Cancel: nothing is sent
 * If the check itself fails, the files go as they are.
 */

const MEDIA_EXT = /\.(jpe?g|png|webp|gif|heic|mp4|webm|mov|avi|mkv|mp3|m4a|wav|ogg|oga|aac|opus)$/i;

export const isDocumentFile = (file) => {
    const type = String(file?.type || '');
    if (/^(image|video|audio)\//.test(type)) return false;
    return !MEDIA_EXT.test(String(file?.name || ''));
};

/** The same bytes under another name. */
export const renameFile = (file, name) => new File([file], name, { type: file.type, lastModified: file.lastModified });

const escapeHtml = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

export const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

const sentOn = (at) => {
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return '';
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
};

/** Exactly the size of the file being sent: a strong hint it has not changed. */
export const sameSize = (version, file) => Boolean(version?.size && file?.size && version.size === file.size);

/**
 * Asks which way to go for each match. Resolves { [name]: 'upload' | versionIndex },
 * or null on Cancel.
 */
const askChoices = async (matches, files) => {
    const fileOf = (name) => files.find((f) => f.name === name);
    const rows = matches.map((m, i) => {
        const file = fileOf(m.name);
        const versions = m.versions.map((v, j) => {
            const meta = [sentOn(v.sentAt) && `sent ${sentOn(v.sentAt)}`, formatSize(v.size)].filter(Boolean).join(' · ');
            return `
                <label class="snf-option">
                    <input type="radio" name="snf-${i}" value="${j}">
                    <span>
                        <strong>${escapeHtml(v.fileName)}</strong>
                        ${sameSize(v, file) ? '<em class="snf-same">Same size as yours</em>' : ''}
                        ${meta ? `<br><small>${escapeHtml(meta)}</small>` : ''}
                    </span>
                </label>`;
        }).join('');
        const now = formatSize(file?.size);
        return `
            <fieldset class="snf-row">
                <legend class="snf-name">${escapeHtml(m.name)}</legend>
                <div class="snf-group-label">Send a file you sent before (nothing uploads)</div>
                ${versions}
                <div class="snf-group-label">Or</div>
                <label class="snf-option">
                    <input type="radio" name="snf-${i}" value="upload" checked>
                    <span><strong>Upload this one</strong>${now ? ` <small>(${escapeHtml(now)})</small>` : ''}<br><small>Sent as <b>${escapeHtml(m.copyName)}</b></small></span>
                </label>
            </fieldset>`;
    }).join('');

    const result = await Swal.fire({
        title: matches.length === 1 ? 'You sent this file before' : 'You sent these files before',
        html: `<div class="snf-list">${rows}</div>`,
        showCancelButton: true,
        confirmButtonText: 'Send',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#215D7B',
        focusConfirm: true,
        customClass: { popup: 'snf-popup' },
        preConfirm: () => Object.fromEntries(matches.map((m, i) => {
            const value = document.querySelector(`input[name="snf-${i}"]:checked`)?.value || 'upload';
            return [m.name, value === 'upload' ? 'upload' : Number(value)];
        }))
    });
    return result.isConfirmed ? result.value : null;
};

/** Puts the choices into effect: earlier versions to send again, and files to upload (renamed). */
export const applyChoices = (files, matches, choices) => {
    const byName = new Map(matches.map((m) => [m.name, m]));
    const reuse = [];
    const outgoing = [];
    (files || []).forEach((file) => {
        const match = isDocumentFile(file) ? byName.get(file.name) : null;
        const choice = match ? choices[file.name] : 'upload';
        const version = match && typeof choice === 'number' ? match.versions[choice] : null;
        if (!match) {
            outgoing.push(file);
        } else if (version) {
            reuse.push({ messageId: version.messageId, attachmentId: version.attachmentId, fileName: version.fileName });
        } else {
            outgoing.push(renameFile(file, match.copyName));
        }
    });
    return { files: outgoing, reuse };
};

export const resolveSameNameFiles = async (conversationId, files) => {
    const documents = (files || []).filter(isDocumentFile);
    if (!documents.length) return { files, reuse: [] };

    let matches = [];
    try {
        const res = await api.post(`/conversations/${conversationId}/file-matches`, {
            names: documents.map((f) => f.name)
        });
        matches = (res.data?.matches || []).filter((m) => m.versions?.length);
    } catch {
        return { files, reuse: [] }; // the check is a convenience; never block a send on it
    }
    if (!matches.length) return { files, reuse: [] };

    const choices = await askChoices(matches, documents);
    if (!choices) return null;
    return applyChoices(files, matches, choices);
};
